import { addHours, format, parse } from 'date-fns'

// Formato estrellas (Tab hoteles)
export const splitEstrellas = (textoEstrellas: string | undefined): { countEstrellas: number, rightString: string | null } => {
  const parts = textoEstrellas?.split(' ') ?? [] // dividir el string en un array
  const countEstrellas = parts[0]?.split('*')?.length ? parts[0].split('*').length - 1 : 0 // obtener la cantidad de asteriscos
  const rightString = parts[1] || null // obtener el string de la parte derecha del espacio, si existe
  return { countEstrellas, rightString }
}

// Obtener array precios
export const extractIdAndPrice = (
  hotels: HotelRecommended[]
): Array<{ id: number; price: string; tax_total_price?: string }> => {
  return hotels?.map(hotel => ({
    id: hotel.id,
    price: hotel.price,
    tax_total_price: hotel.tax_total_price ?? hotel.price
  }))
}

// Obtener precio desde id (con tasas si useTotalPrice y hay tax_total_price)
export const getPriceById = (
  id: number,
  hotels: Array<{ id: number; price: string; tax_total_price?: string }>,
  useTotalPrice = false
): string | undefined => {
  const hotel = hotels.find(h => h.id === id)
  if (!hotel) return undefined
  const value = useTotalPrice && hotel.tax_total_price ? hotel.tax_total_price : hotel.price
  if (value == null || String(value).trim() === '') return undefined
  return String(value)
}

// Obtener info hoteles
export const extractHotelInfo = (dataHoteles: ExtractHotelInfoPayload) => {
  const tabs = dataHoteles?.data?.destination?.map((item) => ({ ...item, label: item.name }))
  const stars = dataHoteles?.data?.stars
  const lista = dataHoteles?.data?.hotel?.reduce<RecommendedHotelListItem[]>((acc, hotel) => {
    const count = acc.filter(h => h.destination === hotel.destination)?.length
    if (count < 4) {
      acc.push(hotel)
    }
    return acc
  }, [])
  const precio = extractIdAndPrice(lista as unknown as HotelRecommended[])

  return { tabs, stars, lista, precio }
}

function getCityCoordinates(
  city: CitiesRecommended,
  ordenCiudades: StrapiDestination[],
): GeoCoordinates | null {
  const fromCity = toGeoCoordinates(city.latitude, city.longitude)
  if (fromCity) return fromCity

  const strapiCity = ordenCiudades.find((o: StrapiDestination) => o.identifier === city.id)
  return toGeoCoordinates(strapiCity?.latitude, strapiCity?.longitude)
}

function compareByStrapiOrder(
  a: CitiesRecommended,
  b: CitiesRecommended,
  ordenCiudades: StrapiDestination[],
): number {
  const ordenA = ordenCiudades.find((o: StrapiDestination) => o.identifier === a.id)?.order ?? 0
  const ordenB = ordenCiudades.find((o: StrapiDestination) => o.identifier === b.id)?.order ?? 0
  return ordenA - ordenB
}

/** Ordena ciudades de más cercana a más lejana respecto a la ubicación GeoIP del usuario. */
export const ordenarCiudades = (
  ciudades: CitiesRecommended[],
  userLocation: GeoCoordinates | null | undefined,
  ordenCiudades: StrapiDestination[] = [],
): CitiesRecommended[] => {
  const list = [...(ciudades ?? [])]

  if (userLocation && isValidGeoCoordinates(userLocation.latitude, userLocation.longitude)) {
    return list.sort((a, b) => {
      const coordsA = getCityCoordinates(a, ordenCiudades)
      const coordsB = getCityCoordinates(b, ordenCiudades)

      if (!coordsA && !coordsB) return compareByStrapiOrder(a, b, ordenCiudades)
      if (!coordsA) return 1
      if (!coordsB) return -1

      const distanceA = haversineDistanceKm(userLocation, coordsA)
      const distanceB = haversineDistanceKm(userLocation, coordsB)

      if (distanceA !== distanceB) return distanceA - distanceB
      return compareByStrapiOrder(a, b, ordenCiudades)
    })
  }

  return list.sort((a, b) => compareByStrapiOrder(a, b, ordenCiudades))
}

type SearchSortComparator = (a: Partial<Hotel> | null | undefined, b: Partial<Hotel> | null | undefined) => number

// Ordenar hoteles search
export const ordenarSearch: Array<SearchSortComparator | null> = [
  null, // Sin orden (Relevancia)
  (a, b) => { // Precio (menor a mayor)
    if (!a || !b) return 0
    return primerPrecio(a) - primerPrecio(b)
  },
  (a, b) => { // Precio (mayor a menor)
    if (!a || !b) return 0
    return ultimoPrecio(b) - ultimoPrecio(a)
  },
  (a, b) => { // Estrellas (menor a mayor)
    return Number(a?.stars?.order) - Number(b?.stars?.order)
  },
  (a, b) => { // Estrellas (mayor a menor)
    return Number(b?.stars?.order) - Number(a?.stars?.order)
  }
]

export type SearchPackHours = 3 | 6 | 24

export type PackMinPriceSlotLike = {
  price?: number
  price_label?: string
  total_price?: number
  total_price_label?: string
}

/** Precio a mostrar en UI: total con tasas si el AB test lo pide y hay dato. */
export function packSlotDisplayPrice(
  slot: PackMinPriceSlotLike | undefined,
  useTotalPrice = false
): { price?: number; label: string } {
  if (useTotalPrice && typeof slot?.total_price === 'number') {
    return {
      price: slot.total_price,
      label: slot.total_price_label || slot.price_label || ''
    }
  }
  if (useTotalPrice && slot?.total_price_label) {
    return { price: slot.total_price, label: slot.total_price_label }
  }
  return {
    price: typeof slot?.price === 'number' ? slot.price : undefined,
    label: slot?.price_label || ''
  }
}

// Precio numérico de un pack concreto; sin pack, el mínimo entre packs disponibles.
export const precioForPack = (
  hotel: Partial<Hotel> | null | undefined,
  pack: SearchPackHours | null | undefined,
  useTotalPrice = false
): number => {
  if (!hotel) return 0
  if (pack) {
    const { price } = packSlotDisplayPrice(hotel.pack_min_price?.[pack], useTotalPrice)
    return price ?? 0
  }
  return primerPrecio(hotel, useTotalPrice)
}

// Etiqueta de precio de un pack concreto; sin pack, la del primer pack disponible.
export const precioLabelForPack = (
  hotel: Pick<Partial<Hotel>, 'pack_min_price'> | null | undefined,
  pack: SearchPackHours | null | undefined,
  useTotalPrice = false
): string => {
  if (!hotel) return ''
  if (pack) return packSlotDisplayPrice(hotel.pack_min_price?.[pack], useTotalPrice).label
  return primerPrecioLabel(hotel, useTotalPrice)
}

// Obtener primer precio del primer pack disponible
export const primerPrecio = (hotel: Partial<Hotel> | null | undefined, useTotalPrice = false): number => {
  if (!hotel) { return 0 }
  const packMinPrice = hotel?.pack_min_price ?? {}
  const firstKeyWithPrice: string | undefined = Object.keys(packMinPrice).find((key) => {
    const displayed = packSlotDisplayPrice(packMinPrice[key], useTotalPrice)
    return displayed.price != null || displayed.label
  })
  const firstPriceOrLabel = firstKeyWithPrice ? packMinPrice[firstKeyWithPrice] : undefined

  return packSlotDisplayPrice(firstPriceOrLabel, useTotalPrice).price ?? 0
}

// Obtener pack hora del primer precio del primer pack disponible
export const primerPrecioPack = (hotel: Partial<Hotel> | null | undefined): string | undefined => {
  if (!hotel) { return undefined }
  const packMinPrice = hotel?.pack_min_price ?? {}
  return Object.keys(packMinPrice).find(key => {
    const slot = packMinPrice[key]
    return slot?.price || slot?.price_label
  })
}

// Obtener primer precio label del primer pack disponible
export const primerPrecioLabel = (
  hotel: Pick<Partial<Hotel>, 'pack_min_price'> | null | undefined,
  useTotalPrice = false
): string => {
  if (!hotel) { return '' }
  const packMinPrice = hotel?.pack_min_price ?? {}
  const firstKeyWithPrice = Object.keys(packMinPrice).find((key) => {
    const displayed = packSlotDisplayPrice(packMinPrice[key], useTotalPrice)
    return displayed.price != null || displayed.label
  })
  const firstPriceOrLabel = firstKeyWithPrice ? packMinPrice[firstKeyWithPrice] : undefined

  return packSlotDisplayPrice(firstPriceOrLabel, useTotalPrice).label
}

// Obtener el último precio válido de un objeto de packs
export const ultimoPrecio = (hotel: Partial<Hotel> | null | undefined): number => {
  if (!hotel) { return 0 }
  const packs = hotel.pack_min_price ?? {}
  const keys = Object.keys(packs)
    .filter(key => typeof packs[key]?.price === 'number' && !isNaN(packs[key]?.price as number))
    .sort((a, b) => Number(a) - Number(b))
  if (keys?.length === 0) return 0
  const lastKey = keys[keys?.length - 1]
  return packs[lastKey ?? '']?.price ?? 0
}

// Obtener array de horarios dado un hotel (keys: 3, 6, 24)
export const getHorasHotel = (
  hotel: { checkin_times?: Hotel['checkin_times'] } | null | undefined,
  idioma: string
): Record<string, string[]> => {
  return Object.keys(hotel?.checkin_times || {}).reduce((acc: Record<string, string[]>, key: string) => {
    const slot = hotel?.checkin_times?.[key] ?? {}
    acc[key] = Object.keys(slot).filter(subKey => slot[subKey] === true).map(subKey => changeHourFormat(idioma) ? format12Hour(subKey) : subKey)
    return acc
  }, {})
}

// Obtiene el primer pack que tiene horarios disponibles
export const getFirstNonEmptyKey = (checkin_times: CheckinTimes) => {
  for (const key in checkin_times) {
    if (Object.keys(checkin_times[key] ?? {}).length > 0) {
      return key
    }
  }
  return null
}

/** Ordena claves de hora (HH:mm o 12h) cronológicamente. */
export function sortCheckinHourKeys(keys: string[]): string[] {
  return [...keys].sort((a, b) =>
    createDateFromString(a).getTime() - createDateFromString(b).getTime()
  )
}

/** Horas de entrada disponibles de un pack, ordenadas cronológicamente. */
export function getAvailableCheckinHourKeys(
  checkinTimesForPack: Record<string, boolean | unknown> | undefined | null
): string[] {
  if (!checkinTimesForPack || objIsEmpty(checkinTimesForPack)) return []
  return sortCheckinHourKeys(
    Object.keys(checkinTimesForPack).filter(key => Boolean(checkinTimesForPack[key]))
  )
}

/** Devuelve la única hora disponible o `null` si hay cero o más de una. */
export function getSingleAvailableCheckinHour(
  checkinTimesForPack: Record<string, boolean | unknown> | undefined | null
): string | null {
  const available = getAvailableCheckinHourKeys(checkinTimesForPack)
  return available.length === 1 ? available[0]! : null
}

/** Primera hora disponible de un pack (`checkin_times[pack]`). */
export function getFirstAvailableHour(
  checkinTimesForPack: Record<string, boolean | unknown> | undefined | null
): string | null {
  return getAvailableCheckinHourKeys(checkinTimesForPack)[0] ?? null
}

/** Última hora disponible de un pack. */
export function getLastAvailableHour(
  checkinTimesForPack: Record<string, boolean | unknown> | undefined | null
): string | null {
  const available = getAvailableCheckinHourKeys(checkinTimesForPack)
  return available[available.length - 1] ?? null
}

/** Suma horas a una etiqueta de hora (24h o 12h AM/PM). */
export function addHoursToTimeLabel(hora: string, cantidad: number): string {
  const is12h = /AM|PM/i.test(hora)
  const date = is12h
    ? parse(hora, 'hh:mm a', new Date())
    : parse(hora, 'HH:mm', new Date())
  const nueva = addHours(date, cantidad)
  return is12h ? format(nueva, 'hh:mm a') : format(nueva, 'HH:mm')
}

/** Extrae la hora (0–23) de una clave de `checkin_times`. */
export function getHourFromTimeKey(key: string): number {
  if (/AM|PM/i.test(key)) {
    return parse(key, 'hh:mm a', new Date()).getHours()
  }
  const parts = key.split(':')
  return parseInt(parts[0] ?? '0', 10)
}

/** `true` si hay entrada disponible en las 24 horas del día (00…23). */
export function isFullDayAvailability(
  checkinTimesForPack: Record<string, boolean | unknown> | undefined | null
): boolean {
  if (!checkinTimesForPack) return false
  const available = Object.keys(checkinTimesForPack).filter(key => Boolean(checkinTimesForPack[key]))
  if (available.length < 24) return false
  const hours = new Set(available.map(getHourFromTimeKey))
  for (let i = 0; i < 24; i++) {
    if (!hours.has(i)) return false
  }
  return true
}

/** Minutos desde medianoche (0–1439) para una clave de `checkin_times`. */
export function timeKeyToMinutes(key: string): number {
  if (/AM|PM/i.test(key)) {
    const parsed = parse(key, 'hh:mm a', new Date())
    return parsed.getHours() * 60 + parsed.getMinutes()
  }
  const parts = key.split(':').map(Number)
  return (parts[0] ?? 0) * 60 + (parts[1] ?? 0)
}

/**
 * Ventana de disfrute de 24h: la salida tras la última entrada + pack coincide con la primera entrada.
 * Ej.: entrada 05:00–23:00 con pack 6h → disfrute 05:00–05:00.
 */
export function isFullDayPackWindow(first: string, last: string, pack: number): boolean {
  if (first === last) return false
  const minutesInDay = 24 * 60
  const firstM = timeKeyToMinutes(first)
  const lastM = timeKeyToMinutes(last)
  return (lastM + pack * 60) % minutesInDay === firstM
}

/** Tipo de promo: 1 hora extra de estancia gratis. */
export const PROMO_TYPE_EXTRA_HOUR = 2

export function getExtraEnjoymentHours(promo?: { type?: number } | null): number {
  return promo?.type === PROMO_TYPE_EXTRA_HOUR ? 1 : 0
}

export function hotelHasExtraHourPromo(
  promos?: Array<{ type?: number }> | null,
  rooms?: Record<string, { promo?: { type?: number } }> | null
): boolean {
  if (promos?.some(p => p.type === PROMO_TYPE_EXTRA_HOUR)) return true
  return Object.values(rooms ?? {}).some(room => getExtraEnjoymentHours(room?.promo) > 0)
}

/**
 * Franja en la que se puede entrar y disfrutar el pack (entrada más temprana → salida tras última entrada + pack).
 * Si hay disponibilidad 00–23 o la ventana de disfrute cubre 24h, devuelve `allDayLabel`.
 * `extraEnjoymentHours` suma horas de promo (p. ej. +1h gratis, tipo 2) en packs 3h y 6h.
 * En el pack 24h no aplica: la franja refleja el rango de entrada, no la salida tras la estancia.
 */
export function getPackWindowLabel(
  pack: number,
  checkinTimesForPack: Record<string, boolean | unknown> | undefined | null,
  idioma: string,
  allDayLabel: string,
  extraEnjoymentHours = 0
): string {
  if (!checkinTimesForPack || objIsEmpty(checkinTimesForPack)) return ''

  if (isFullDayAvailability(checkinTimesForPack)) {
    return allDayLabel
  }

  const first = getFirstAvailableHour(checkinTimesForPack)
  const last = getLastAvailableHour(checkinTimesForPack)
  if (!first || !last) return ''

  const promoExtra = pack === 24 ? 0 : extraEnjoymentHours
  const enjoymentHours = pack + promoExtra

  if (isFullDayPackWindow(first, last, enjoymentHours)) {
    return allDayLabel
  }

  const use12h = changeHourFormat(idioma)
  const startLabel = use12h ? format12Hour(first) : first
  const endLabel = addHoursToTimeLabel(use12h ? format12Hour(last) : last, enjoymentHours)
  if (startLabel === endLabel) return startLabel
  return `${startLabel} - ${endLabel}`
}

const MINUTES_IN_DAY = 24 * 60

/** Packs de estancia corta; el 24h no entra en la franja agregada del listado. */
const SHORT_STAY_PACK_HOURS = [3, 6] as const

/** Horas de entrada disponibles de un hotel/habitación, tal cual las trae la API (todos los packs: 3h/6h/24h). */
export function getCheckinHourKeys(
  checkinTimes: Record<string, Record<string, boolean | unknown>> | undefined | null
): string[] {
  if (!checkinTimes) return []
  const hours = new Set<string>()
  for (const slot of Object.values(checkinTimes)) {
    if (!slot) continue
    for (const key of Object.keys(slot)) {
      if (slot[key]) hours.add(key)
    }
  }
  return [...hours]
}

function minutesToTimeKey(minutes: number): string {
  const normalized = ((minutes % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY
  const h = Math.floor(normalized / 60)
  const m = normalized % 60
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
}

function formatTimeKeyForIdioma(timeKey: string, idioma: string): string {
  return changeHourFormat(idioma) ? format12Hour(timeKey) : timeKey
}

function enjoymentEndComparableMinutes(endM: number, startM: number): number {
  const normalized = ((endM % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY
  if (normalized <= startM) return normalized + MINUTES_IN_DAY
  return normalized
}

/** Fusiona etiquetas `inicio - fin` teniendo en cuenta horas que cruzan medianoche. */
export function mergeEnjoymentWindowLabels(
  labels: string[],
  idioma: string,
  allDayLabel: string
): string {
  if (!labels.length) return ''
  if (labels.some(label => label === allDayLabel)) return allDayLabel

  const uniqueLabels = [...new Set(labels)]
  if (uniqueLabels.length === 1) return uniqueLabels[0]!

  let minStartM = Infinity
  let maxEndComparable = -Infinity

  for (const label of uniqueLabels) {
    const parts = label.split(' - ').map(part => part.trim())
    const startM = timeKeyToMinutes(parts[0]!)
    minStartM = Math.min(minStartM, startM)

    if (parts.length === 1) {
      maxEndComparable = Math.max(maxEndComparable, enjoymentEndComparableMinutes(startM, startM))
      continue
    }

    const endM = timeKeyToMinutes(parts[1]!)
    maxEndComparable = Math.max(maxEndComparable, enjoymentEndComparableMinutes(endM, startM))
  }

  if (minStartM === Infinity || maxEndComparable < 0) return labels[0] ?? ''

  const startLabel = formatTimeKeyForIdioma(minutesToTimeKey(minStartM), idioma)
  const endLabel = formatTimeKeyForIdioma(minutesToTimeKey(maxEndComparable), idioma)

  if (startLabel === endLabel) return startLabel
  return `${startLabel} - ${endLabel}`
}

function getEnjoymentWindowLabelForCheckinTimes(
  checkinTimes: Record<string, Record<string, boolean | unknown>> | undefined | null,
  availablePacks: number[],
  idioma: string,
  allDayLabel: string,
  extraEnjoymentHours = 0
): string {
  if (!checkinTimes || !availablePacks.length) return ''

  const shortAvailablePacks = availablePacks.filter(pack =>
    (SHORT_STAY_PACK_HOURS as readonly number[]).includes(pack)
  )

  if (!shortAvailablePacks.length) return ''

  const packLabels = shortAvailablePacks
    .map(pack => getPackWindowLabel(pack, checkinTimes[String(pack)], idioma, allDayLabel, extraEnjoymentHours))
    .filter(Boolean)

  if (!packLabels.length) return ''

  if (packLabels.some(label => label === allDayLabel)) {
    return allDayLabel
  }

  const uniqueLabels = [...new Set(packLabels)]
  if (uniqueLabels.length === 1) {
    return uniqueLabels[0]!
  }

  return mergeEnjoymentWindowLabels(uniqueLabels, idioma, allDayLabel)
}

/**
 * Ventana de disfrute agregada para listados (solo packs 3h y 6h).
 * El pack 24h no se incluye en el sobre.
 * Si hay habitaciones con `checkin_times`, se calcula por habitación y se fusiona
 * (más fiel que el agregado a nivel hotel, que puede incluir horas espurias).
 */
export function getHotelEnjoymentWindowLabel(
  checkinTimes: Record<string, Record<string, boolean | unknown>> | undefined | null,
  availablePacks: number[],
  idioma: string,
  allDayLabel: string,
  rooms?: Record<string, {
    checkin_times?: Record<string, Record<string, boolean | unknown>>
    promo?: { type?: number }
  }> | null,
  hotelPromos?: Array<{ type?: number }> | null
): string {
  if (rooms && Object.keys(rooms).length > 0) {
    const roomLabels = Object.values(rooms)
      .map(room => getEnjoymentWindowLabelForCheckinTimes(
        room?.checkin_times,
        availablePacks,
        idioma,
        allDayLabel,
        getExtraEnjoymentHours(room?.promo)
      ))
      .filter(Boolean)

    if (roomLabels.length > 0) {
      return mergeEnjoymentWindowLabels(roomLabels, idioma, allDayLabel)
    }
  }

  const hotelExtraHours = hotelHasExtraHourPromo(hotelPromos, rooms) ? 1 : 0
  return getEnjoymentWindowLabelForCheckinTimes(
    checkinTimes,
    availablePacks,
    idioma,
    allDayLabel,
    hotelExtraHours
  )
}

/**
 * Franja de disponibilidad de un hotel para el filtro de pack.
 * - Sin pack (Todos): ventana de disfrute agregada (packs cortos 3h/6h).
 * - Pack 3h/6h: ventana de disfrute de ese pack (con refinamiento por habitación).
 * - Pack 24h: rango de entrada del pack (no aplica ventana de disfrute).
 */
export function getPackAvailabilityWindowLabel(
  pack: number | null | undefined,
  checkinTimes: Record<string, Record<string, boolean | unknown>> | undefined | null,
  availablePacks: number[],
  idioma: string,
  allDayLabel: string,
  rooms?: Record<string, {
    checkin_times?: Record<string, Record<string, boolean | unknown>>
    promo?: { type?: number }
  }> | null,
  hotelPromos?: Array<{ type?: number }> | null
): string {
  if (!pack) {
    return getHotelEnjoymentWindowLabel(checkinTimes, availablePacks, idioma, allDayLabel, rooms, hotelPromos)
  }
  if (pack === 24) {
    return getPackWindowLabel(24, checkinTimes?.['24'], idioma, allDayLabel)
  }
  return getHotelEnjoymentWindowLabel(checkinTimes, [pack], idioma, allDayLabel, rooms, hotelPromos)
}

// Diferencia entre horas
export const diffHours = (start: string, end: string): number => {
  const startDate = createDateFromString(start)
  const endDate = createDateFromString(end)
  const difference = endDate.getTime() - startDate.getTime()
  return difference / (1000 * 60 * 60)
}

export const createDateFromString = (time: string) => {
  const parts = time.split(':').map(Number)
  const h = parts[0] ?? 0
  const m = parts[1] ?? 0
  return new Date(0, 0, 0, h, m)
}

// Obtener query de url de un destination
export const getDestinationInfo = async (destination_id: number, locale: Ref): Promise<{ query: string, destinationData: Destination | null}> => {
  if (destination_id) {
    const { data: destinationData } = await useFetch<DestinationIdApiResponse>(`${useBaseUrl()}/destination-id`, {
      key: `destination-${destination_id}`,
      headers: { 'Content-Type': 'application/json' },
      query: {
        destination_id,
        locale
      },
      getCachedData: (key, nuxt) => nuxt.payload.data[key],
      onResponseError(res) {
        logError(res)
      }
    })

    if (!destinationData.value?.data) {
      console.error('Error: No se han recibido datos de /destination-id')
      return { query: '', destinationData: null }
    }

    const objQuery: Record<string, string | number | undefined | null> = {
      'data-destinationId': destinationData.value?.data?.element_id,
      'data-distance': destinationData.value?.data?.search_distance,
      'data-search': destinationData.value?.data?.name_translated,
      'data-country': destinationData.value?.data?.country_name_translated,
      'search': destinationData.value?.data?.name_translated
    }

    return {
      query: Object.keys(objQuery).map(key => (objQuery[key] ? `${key}=${objQuery[key]}` : '')).filter(Boolean).join('&'),
      destinationData: destinationData.value?.data ?? null
    }
  }
  return { query: '', destinationData: null }
}

export const showExtraBetween = <T extends { start_time: string, end_time: string }>(ranges: Array<T>, time: string): boolean => {

  if (ranges.length === 0) return true

  const [hours, minutes] = time.split(':').map(Number)
  const givenTime = new Date()
  givenTime.setHours(hours ?? 0, minutes ?? 0, 0, 0)

  const show = ranges.some((range) => {
    const [startHours, startMinutes] = range.start_time.split(':').map(Number)
    const [endHours, endMinutes] = range.end_time.split(':').map(Number)

    const startTime = new Date()
    startTime.setHours(startHours ?? 0, startMinutes ?? 0, 0, 0)

    const endTime = new Date()
    endTime.setHours(endHours ?? 0, endMinutes ?? 0, 0, 0)

    return givenTime >= startTime && givenTime <= endTime
  })

  return show
}

/** Extras del hotel: la API usa `extra` en ficha y `extras` en otros contextos. */
export function getHotelExtras(hotel: Hotel | null | undefined): Extra[] {
  if (!hotel) return []
  return hotel.extra ?? hotel.extras ?? []
}

export type ExtraScheduleRange = ExtraSchedule

/** Etiqueta legible de franjas horarias de un extra (p. ej. "10:00 - 14:00, 16:00 - 20:00"). */
export function formatExtraSchedulesLabel(
  ranges: ExtraSchedule[] | undefined,
  idioma: string,
): string {
  if (!ranges?.length) return ''
  return ranges
    .map(({ start_time, end_time }) => {
      const start = formatDisplayHour(start_time, idioma)
      const end = formatDisplayHour(end_time, idioma)
      return `${start} - ${end}`
    })
    .join(', ')
}

export function removeCookieFileds(data: Destination): Destination {
  const newData = { ...(data as unknown as Record<string, unknown>) }
  delete newData.embeded_html // quitar parametros por tamaña de cookie
  delete newData.description
  delete newData.locationPickKind
  newData.label = newData.name_translated // añadir label
  return newData as unknown as Destination
}

// Función para verificar si checkin_times está vacío
export const isCheckinTimesEmpty = (checkin_times: Record<string, Record<string, unknown>>) => {
  return Object.values(checkin_times).every((obj) => objIsEmpty(obj))
}

// Función para verificar si un objeto está vacío
export const areAllInnerObjectsEmpty = (obj: Record<string, Record<string, unknown>>): boolean => {
  return Object.values(obj).every((innerObj) => Object.keys(innerObj).length === 0)
}
